import {
  ActiveOrder,
  MapCenter,
  MapCoordinate,
  MapCoordinates,
} from '@/resources/types'
import emotionTheme from '@/styles/theme'
import {
  getOrderTimeColorHex,
  getScheduledColorTextByTime,
  scheduledTimeColors,
} from '@/services/orders/colors'
import { getCardColorByType } from '@/pages/Orders/utils'
import { DeliveryType } from '@/resources/enums/deliveryTypes'
import iconReceiveFilter from '@/assets/imgs/icon-receive-gray.svg'
import iconScheduledFilter from '@/assets/imgs/icon-scheduled.svg'
import iconTurboFilter from '@/assets/imgs/icon-turbo.svg'
import iconPinPoc from '@/assets/imgs/map-pin-poc.svg'
import { Location } from '@/resources/types/seller'
import { OrderStatus } from '@/resources/enums'
import { ORDERS_MAP as dict } from '@/resources/dict'
import {
  OrderColors,
  OrderColorHex,
  TurboColor,
} from '@/resources/enums/orders'

const isGoogleMapLoaded = () => 'google' in window && !!google?.maps

export interface ObtainCustomizedMapProps {
  mapDiv: HTMLElement
  mapId: string
  lat: number
  lng: number
  scrollwheel?: boolean
}

export interface MapFilterOption {
  label: string
  value: string
}

export interface MapSelectedOptions {
  [value: string]: boolean
}

export interface MapFilterProps {
  options: MapFilterOption[]
  checkedOptions: MapSelectedOptions
  onFiltersChange(option: string, value: boolean): void
}

export const MAP_PIN_BLACK_COLOR = 'black'

export type MapPinColor = OrderColors | typeof MAP_PIN_BLACK_COLOR

export type MapPinCallback = (order: ActiveOrder, color: MapPinColor) => void

export const filterLabelByOption: { [key: string]: string } = {
  [DeliveryType.RECEIVE]: dict.filters.receive,
  [DeliveryType.SCHEDULED]: dict.filters.scheduled,
  [DeliveryType.TURBO]: dict.filters.turbo,
}

export const getMapFilterOptions = (
  hasTurbo: boolean,
  hasScheduled: boolean
): MapFilterOption[] => {
  const types = [DeliveryType.RECEIVE]
  if (hasTurbo) types.push(DeliveryType.TURBO)
  if (hasScheduled) types.push(DeliveryType.SCHEDULED)

  return types.map(type => ({
    label: filterLabelByOption[type],
    value: type,
  }))
}

export const mapFilterIcons: { [type: string]: string } = {
  [DeliveryType.RECEIVE]: iconReceiveFilter,
  [DeliveryType.SCHEDULED]: iconScheduledFilter,
  [DeliveryType.TURBO]: iconTurboFilter,
}

export const obtainCustomizedMap = ({
  mapDiv,
  mapId,
  lat,
  lng,
  scrollwheel = false,
}: ObtainCustomizedMapProps) => {
  if (isGoogleMapLoaded()) {
    return new google.maps.Map(mapDiv, {
      center: { lat, lng },
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
      scrollwheel,
      mapId,
    })
  }

  return null
}

export interface ObtainCustomMarkerProps {
  lat: number
  lng: number
  map: google.maps.Map | null
  icon: { url: string }
  title?: string
}

export const obtainCustomMarker = ({
  lat,
  lng,
  map,
  icon,
  title = 'marker',
}: ObtainCustomMarkerProps) => {
  if (isGoogleMapLoaded()) {
    const position = new google.maps.LatLng(lat, lng)
    const iconMarker = document.createElement('img')
    iconMarker.src = icon.url

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: iconMarker,
      title,
    })
  }

  return null
}

export interface MountPolygonsProps {
  coordinates: Array<Array<MapCoordinates>>
}

export const mountPolygons = (
  { coordinates }: MountPolygonsProps,
  map: google.maps.Map
) => {
  if (isGoogleMapLoaded()) {
    coordinates.forEach((coordinate = []) => {
      const pointArr =
        coordinate?.flatMap(geoCoordinate => geoCoordinate) ?? coordinate[0]
      const pol = new google.maps.Polygon({
        paths: pointArr.map(point => ({ lat: point[1], lng: point[0] })),
        strokeColor: emotionTheme.ds.ColorBaseBlack,
        strokeOpacity: 0.3,
        strokeWeight: 3,
        fillColor: '#fcd500',
        fillOpacity: 0.18,
      })
      pol.setMap(map)
    })
  }
}

export const obtainMapCenterFromMapCoordinate = (
  coordinates: MapCoordinate
): MapCenter | null => {
  try {
    const mapCenter: MapCenter = {
      lat: coordinates?.[1],
      lng: coordinates?.[0],
    }
    const isValidCoordinates =
      typeof mapCenter.lat === 'number' && typeof mapCenter.lng === 'number'

    if (!isValidCoordinates)
      throw new Error(
        `Invalid coordinates!\nResult: ${JSON.stringify(mapCenter)}`
      )
    return mapCenter
  } catch (error) {
    console.error(error)
    return null
  }
}

export const generateCirclePolygonCoordinates = (
  center: MapCenter | null,
  radiusKm: number,
  points: number = 64
): MapCoordinate[] | null => {
  if (!center || !radiusKm || radiusKm <= 0) return null

  const coordinates: MapCoordinate[] = []
  const earthRadiusKm = 6371

  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points
    const angleRad = (angle * Math.PI) / 180

    const latRad = (center.lat * Math.PI) / 180
    const lngRad = (center.lng * Math.PI) / 180

    const sinRadiusRatio = Math.sin(radiusKm / earthRadiusKm)
    const cosRadiusRatio = Math.cos(radiusKm / earthRadiusKm)

    const newLatRad = Math.asin(
      Math.sin(latRad) * cosRadiusRatio +
        Math.cos(latRad) * sinRadiusRatio * Math.cos(angleRad)
    )

    const newLngRad =
      lngRad +
      Math.atan2(
        Math.sin(angleRad) * sinRadiusRatio * Math.cos(latRad),
        cosRadiusRatio - Math.sin(latRad) * Math.sin(newLatRad)
      )

    const newLat = (newLatRad * 180) / Math.PI
    const newLng = (newLngRad * 180) / Math.PI

    coordinates.push([newLng, newLat])
  }

  if (coordinates.length > 0) {
    coordinates.push(coordinates[0])
  }

  return coordinates
}

export const getOrderMarkerColor = ({
  status,
  time,
  deliveryType,
  deliveryDate,
}: {
  status: OrderStatus
  time: number
  deliveryType: DeliveryType
  deliveryDate?: Date
}): string => {
  if (deliveryType === DeliveryType.ECONOMY) {
    return emotionTheme.ds.ColorBaseBlack
  }

  const minutes = time / 60
  let color = getOrderTimeColorHex(
    status,
    minutes,
    deliveryType === DeliveryType.TURBO
  )

  if (DeliveryType.SCHEDULED === deliveryType && deliveryDate) {
    color =
      getScheduledColorTextByTime(deliveryDate) ??
      scheduledTimeColors.GREEN.text
  }

  return color
}

export const getOrderMarkerPinSvgDataUri = (svgString: string) => {
  const encoded = encodeURIComponent(svgString)
  return `data:image/svg+xml,${encoded}`
}

export const getMap = (mapId: string, pocCoordinates?: Location) => {
  const configMapId = process.env.GOOGLE_MAPS_ID ?? mapId

  return obtainCustomizedMap({
    mapDiv: document.getElementById(mapId) || ({} as HTMLElement),
    mapId: configMapId,
    lat: pocCoordinates?.coordinates[1] ?? 0,
    lng: pocCoordinates?.coordinates[0] ?? 0,
    scrollwheel: true,
  })
}

export const getPocMarker = (map: google.maps.Map, pocCoordinates: Location) =>
  obtainCustomMarker({
    lat: pocCoordinates.coordinates[1],
    lng: pocCoordinates.coordinates[0],
    map,
    icon: {
      url: iconPinPoc,
    },
  })

export const setMapBounds = (
  map: google.maps.Map,
  markers: Array<google.maps.marker.AdvancedMarkerElement | null>
) => {
  const bounds = new google.maps.LatLngBounds()

  markers.forEach(marker => {
    const position = marker?.position

    if (position) bounds.extend(position)
  })

  map.fitBounds(bounds)
}

const mapPinColorByHex: Record<string, MapPinColor> = {
  [emotionTheme.ds.ColorBaseBlack]: MAP_PIN_BLACK_COLOR,
  [OrderColorHex.GREEN]: OrderColors.GREEN,
  [OrderColorHex.YELLOW]: OrderColors.YELLOW,
  [OrderColorHex.RED]: OrderColors.RED,
}

export const getColorNameByHex = (colorHex: string): MapPinColor =>
  mapPinColorByHex[colorHex] ?? OrderColors.RED

export const addColorBorderToMapTooltip = (
  orderNumber: string,
  deliveryType: DeliveryType,
  turboColor?: TurboColor
) => {
  const tooltipCards = document.querySelectorAll('.gm-style-iw-c') ?? []
  const card = Array.from(tooltipCards).find(element =>
    element.querySelector(`#tooltip-order-${orderNumber}`)
  ) as HTMLElement
  if (card) {
    card.style.boxShadow = `inset 5px 0px ${getCardColorByType(deliveryType, turboColor)}`
  }
}

export const getMapOffsetForTooltipSize = (productsLength: number) => {
  const defaultOffset = -45
  const productsSizeOffset = 10
  const size = productsLength >= 4 ? 4 : productsLength
  return defaultOffset - size * productsSizeOffset
}
